import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsVideosStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    titleText?: TypographyProps;
    typographyProps?: TypographyProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
}

export const drawingsStyles: ProductDetailsVideosStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
           flex-direction: column;
    `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            padding-top: 10px;
            font-weight: 700;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
            font-size: 16px;
            padding-top: 10px;
        `,
    },
    typographyProps: {
        css: css`
            text-align: left;
            font-size: 15px;
            font-weight: 700;
            color: #094e66;
            text-decoration: none;
        `,
    },
};

const styles = drawingsStyles;

const ProductDetailsVideos: React.FC<OwnProps> = ({ product: { id, productNumber, documents } }) => {
    if (!id || !documents) {
        return null;
    }

    const videos = documents.filter(d => d.documentType.includes("video"));

    if (!videos) {
        return null;
    }

    const getIframeSrc = (filePath: string) => {
        if (filePath.includes("http")) {
            filePath = filePath.replace("http", "https");
        } else {
            filePath = `https://www.youtube.com/embed/${filePath}`;
        }
        return filePath;
    };

    return (
        <ManagedAccordionSection
            title={translate("Videos")}
            data-test-selector="productDetails_videos"
            initialExpanded={false}
            {...styles.specificationsAccordionSection}
        >
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.container}>
                    {videos.map(video => (
                        <GridItem {...styles.documentGridItem} key={`video_${video.id}`}>
                            <iframe
                                key={video.id}
                                width="100%"
                                height="100%"
                                src={getIframeSrc(video.filePath)}
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        <Typography {...styles.documentNameText}>{video.name}</Typography>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        </ManagedAccordionSection>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsVideos),
    definition: {
        displayName: "Videos",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
